import * as autoPrintReceiptsStorage from "../../lib/autoPrintReceiptsStorage";
import * as displayPlugin from "../../lib/DisplayPlugin/DisplayPlugin";
import * as posActions from "../../lib/posActions";
import * as posMenuEndpoints from "../../lib/api/menus";
import * as posSettings from "../../lib/api/settings";
import * as posStoreEndpoints from "../../lib/api/stores";
import * as routeHelpers from "../../lib/routeHelpers";
import * as Sentry from "@sentry/capacitor";
import CashierCheckout from "../CashierCheckout";
import CashPull from "../CashPull";
import describeNetworkError from "../../lib/describeNetworkError";
import ErrorModal from "../ErrorModal";
import logger from "../../lib/sentryLogger";
import React, {useEffect, useCallback, useContext, useState} from "react";
import Spinner from "../Spinner";
import styles from "./App.module.css";
import useAppActivityManager from "../../lib/useAppActivityManager";
import usePrevious from "../../lib/usePrevious";
import useRefreshPendingOrders from "../../lib/useRefreshPendingOrders";
import {API, Actions} from "habit-core";
import {Device} from "@capacitor/device";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";
import {RefreshContext} from "../RefreshContext";
import {setInstanceHeader} from "../../lib/api/instance";

const REQUIRED_DPI = 160;

const strings = {
    genericErrorTitle: "Whoops!",
    genericErrorMessage: "Something went wrong. Please try again.",
    retry: "Retry",
    discountErrorModalTitle: "Discount(s) Automatically Removed",
    dpiErrorModalTitle: "Display Configuration Warning",
    dpiDetectedWarningMessage: (detectedDpi: number) =>
        `This device's display configuration is incorrect and may result in some visual issues. The POS can still be used, but please contact IT. \n Issue: DPI is set to ${detectedDpi}. It should be set to ${REQUIRED_DPI}.`,
    dpiUndetectedWarningMessage:
        "This device's display configuration could not be read & may have some issues. The POS can still be used but please contact IT.",
    reserveOrderIdErrorMessage:
        "Unable to get a check ID for the selected action. Please click retry below. If the error persists, please notify the help desk.",
    dismiss: "Dismiss",
    lastCartEntityRemovalErrorTitle: "Final Item Cannot Be Removed",
};

// TODO: #188 - Think about how we want to load this onto pos config
const requiresDriveThruDetails = true;

export default function App() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [initialLoadErrorMessage, setInitialLoadErrorMessage] = useState("");
    const [dpiWarningMessage, setDpiWarningMessage] = useState("");
    const dispatch = useAppDispatch();

    const currentOrder = useAppSelector((state) => state.currentOrder);
    const stationMode = useAppSelector((state) => state.pos.station.mode);
    const storeId = useAppSelector((state) => state.pos.store.id);
    const discountErrorMessage = useAppSelector(
        (state) => state.pos.errors.discountErrorMessage,
    );
    const reserveOrderIdErrored = useAppSelector(
        (state) => state.pos.errors.reserveOrderIdErrored,
    );
    const lastCartEntityRemovalErrorMessage = useAppSelector(
        (state) => state.pos.errors.lastCartEntityRemovalErrorMessage,
    );

    const {
        globalMenuLastUpdateDateRef,
        storeMenuLastUpdateDateRef,
        storeSettingsLastUpdateDateRef,
    } = useContext(RefreshContext);

    const isTraining = useAppSelector((state) => state.pos.station.isTraining);

    const prevIsTraining = usePrevious(isTraining);

    const [isAfterInitialLoad, setIsAfterInitialLoad] = useState(false);
    const initialLoad = useCallback(() => {
        setLoading(true);
        Promise.all([
            Device.getId(),
            posSettings.get(),
            dispatch(posActions.loadStoreSettings()),
            displayPlugin.getDPI(),
            autoPrintReceiptsStorage.getStoredAutoPrintReceipts(),
        ])
            .then(
                ([
                    deviceIdResp,
                    settings,
                    storeSettings,
                    dpi,
                    autoPrintReceiptsStoredValue,
                ]) => {
                    // configure sentry tags + set logger store number so it is included with every log sent to sentry.
                    logger.setStoreNumber(storeSettings.storeNumber);
                    Sentry.setTag("storeNumber", storeSettings.storeNumber);
                    Sentry.setTag("stationMode", settings.stationMode);
                    Sentry.setTag("deviceId", deviceIdResp.identifier);

                    // add headers to instance
                    setInstanceHeader(
                        "X-POS-Station-Mode",
                        settings.stationMode,
                    );
                    setInstanceHeader(
                        "X-POS-Device-Id",
                        deviceIdResp.identifier,
                    );

                    if (dpi === undefined) {
                        logger.warn("Unable to detect the device DPI");
                        setDpiWarningMessage(
                            strings.dpiUndetectedWarningMessage,
                        );
                    } else if (dpi !== REQUIRED_DPI) {
                        setDpiWarningMessage(
                            strings.dpiDetectedWarningMessage(dpi),
                        );
                        logger.warn(
                            `The device has a dpi of ${dpi} while we require ${REQUIRED_DPI}.`,
                        );
                    }

                    // backend keeps IP -> station mode mapping. on startup, set mode to what we receive in settings.
                    const stationMode = settings.stationMode;
                    const laneAssignment =
                        stationMode === "drive_thru_order_taker"
                            ? settings.laneAssignment
                            : null;

                    let initialOrderType: API.models.OrderType = "in_store";
                    if (
                        stationMode === "drive_thru_order_taker" ||
                        stationMode === "drive_thru_order_fulfillment" ||
                        stationMode === "line_buster"
                    ) {
                        initialOrderType = "drive_thru";
                    }

                    const storeId = storeSettings.storeId;
                    // set the device id
                    dispatch(posActions.setDeviceId(deviceIdResp.identifier));

                    // initialize the station
                    dispatch(
                        posActions.initializeStation(
                            stationMode,
                            requiresDriveThruDetails,
                            laneAssignment,
                            autoPrintReceiptsStoredValue === "true",
                        ),
                    );

                    return Promise.all([
                        stationMode,
                        posMenuEndpoints.getGlobalMenuLastUpdateDate(),
                        posMenuEndpoints.getStoreMenuLastUpdateDate(storeId),
                        posStoreEndpoints.getStoreSettingsLastUpdateDate(),
                        dispatch(Actions.menuActions.loadGlobalMenu()),
                        dispatch(
                            Actions.currentOrderActions.selectStore(
                                storeId,
                                initialOrderType,
                                false,
                            ),
                        ),
                        dispatch(posActions.initializePosAllPreps(storeId)),
                        dispatch(posActions.initializePosAddOns()),
                    ]);
                },
            )
            .then(
                ([
                    stationMode,
                    globalMenuLastUpdateDate,
                    storeMenuLastUpdateDate,
                    storeSettingsLastUpdateDate,
                ]) => {
                    globalMenuLastUpdateDateRef.current =
                        globalMenuLastUpdateDate;
                    storeMenuLastUpdateDateRef.current =
                        storeMenuLastUpdateDate;
                    storeSettingsLastUpdateDateRef.current =
                        storeSettingsLastUpdateDate;
                    return Promise.all([stationMode]);
                },
            )
            .then(([stationMode]) => {
                const reserverOrderPromise =
                    stationMode !== "drive_thru_order_fulfillment"
                        ? dispatch(posActions.reserveCurrentOrderId())
                        : Promise.resolve();

                return Promise.all([stationMode, reserverOrderPromise]);
            })
            .then(([stationMode]) => {
                setLoading(false);
                navigate(routeHelpers.deviceSetup(), {
                    state: {
                        skipCCRegistrationCheck:
                            stationMode === "drive_thru_order_taker" ||
                            stationMode === "line_buster",
                    },
                });
                setIsAfterInitialLoad(true);
            })
            .catch((err) => {
                const errMsg = describeNetworkError(err).join("\n");
                logger.error("App initial load failed.", {
                    errorMessage: errMsg,
                });
                setInitialLoadErrorMessage(errMsg);
            });
    }, []);

    /* fire once on app startup */
    useEffect(() => {
        initialLoad();
    }, [initialLoad]);

    const location = useLocation();
    // we don't want to poll on checkout step 0 -> step 2
    const disablePollingOnCheckout =
        location.pathname.includes("checkout") &&
        !location.pathname.includes(routeHelpers.otherOrders()) &&
        !location.pathname.includes(routeHelpers.orderComplete());

    const isManagerCancelScreen = location.pathname.includes(
        routeHelpers.cancelOrder(),
    );
    const getPendingOrdersEnabled =
        isAfterInitialLoad &&
        (stationMode === "drive_thru_order_fulfillment" ||
            stationMode === "front_of_house_1" ||
            stationMode === "front_of_house_2") &&
        !disablePollingOnCheckout &&
        !isManagerCancelScreen;

    /* hooks to fetch pending orders */
    useRefreshPendingOrders(
        getPendingOrdersEnabled,
        stationMode === "drive_thru_order_fulfillment" ? "drive_thru" : "kiosk",
        isTraining,
    );

    /* should reserve an order id if isTraining changes and we are not the drive_thru_order_fulfillment role */
    useEffect(() => {
        if (
            isTraining !== prevIsTraining &&
            stationMode !== "drive_thru_order_fulfillment" &&
            isAfterInitialLoad
        ) {
            dispatch(posActions.reserveCurrentOrderId());
        }
    }, [isTraining, prevIsTraining, isAfterInitialLoad, stationMode]);

    const inactiveCallback = useCallback(() => {
        return Promise.all([
            posMenuEndpoints.getGlobalMenuLastUpdateDate(),
            posMenuEndpoints.getStoreMenuLastUpdateDate(storeId ?? ""),
            posStoreEndpoints.getStoreSettingsLastUpdateDate(),
        ])
            .then(
                async ([
                    _globalMenuLastUpdateDate,
                    _storeMenuLastUpdateDate,
                    _storeSettingsLastUpdateDate,
                ]) => {
                    if (
                        globalMenuLastUpdateDateRef.current === null ||
                        globalMenuLastUpdateDateRef.current <
                            _globalMenuLastUpdateDate
                    ) {
                        await dispatch(Actions.menuActions.loadGlobalMenu());
                        await dispatch(posActions.initializePosAddOns());
                        globalMenuLastUpdateDateRef.current =
                            _globalMenuLastUpdateDate;
                    }
                    if (
                        storeMenuLastUpdateDateRef.current === null ||
                        storeMenuLastUpdateDateRef.current <
                            _storeMenuLastUpdateDate
                    ) {
                        await dispatch(
                            posActions.refreshStoreMenu(
                                storeId ?? "",
                                currentOrder.orderType ?? "in_store", // this shouldn't be null
                            ),
                        );
                        await dispatch(
                            posActions.initializePosAllPreps(storeId ?? ""),
                        );
                        storeMenuLastUpdateDateRef.current =
                            _storeMenuLastUpdateDate;
                    }
                    if (
                        storeSettingsLastUpdateDateRef.current === null ||
                        storeSettingsLastUpdateDateRef.current <
                            _storeSettingsLastUpdateDate
                    ) {
                        await dispatch(posActions.loadStoreSettings());
                        storeSettingsLastUpdateDateRef.current =
                            _storeSettingsLastUpdateDate;
                    }
                },
            )
            .catch((err) => {
                const errMsg = describeNetworkError(err).join("\n");
                logger.error(
                    "Failed to refresh menu data and store settings while inactive.",
                    {
                        errorMessage: errMsg,
                    },
                );
            });
    }, [storeId, currentOrder.orderType]);

    useAppActivityManager(inactiveCallback);

    const onRetryInitalLoad = () => {
        setInitialLoadErrorMessage("");
        initialLoad();
    };

    if (initialLoadErrorMessage) {
        return (
            <div>
                <ErrorModal
                    onClose={onRetryInitalLoad}
                    errorMessage={initialLoadErrorMessage}
                    closeButtonLabel={strings.retry}
                    title={strings.genericErrorTitle}
                />
            </div>
        );
    }

    if (dpiWarningMessage) {
        return (
            <div>
                <ErrorModal
                    onClose={() => setDpiWarningMessage("")}
                    errorMessage={dpiWarningMessage}
                    closeButtonLabel={strings.dismiss}
                    title={strings.dpiErrorModalTitle}
                    showITInfo
                />
            </div>
        );
    }

    if (loading) {
        return (
            <div className={styles.loading}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className={styles.app}>
            <CashierCheckout className={styles.cashierCheckout} />
            <Outlet />
            <div className={styles.gitVersion}>
                {import.meta.env.REACT_APP_GIT_REVISION}
                {import.meta.env.DEV ? "-dev" : null}
            </div>
            {discountErrorMessage ? (
                <ErrorModal
                    onClose={() =>
                        dispatch(posActions.setDiscountErrorMessage(""))
                    }
                    errorMessage={discountErrorMessage}
                    title={strings.discountErrorModalTitle}
                    backgroundColor="grey"
                />
            ) : null}
            {reserveOrderIdErrored ? (
                <ErrorModal
                    onClose={() => {
                        dispatch(posActions.reserveCurrentOrderId());
                    }}
                    closeButtonLabel={strings.retry}
                    errorMessage={strings.reserveOrderIdErrorMessage}
                    backgroundColor="red"
                />
            ) : null}
            {lastCartEntityRemovalErrorMessage ? (
                <ErrorModal
                    onClose={() => {
                        dispatch(
                            posActions.setLastCartEntityRemovalErrorMessage(
                                undefined,
                            ),
                        );
                    }}
                    title={strings.lastCartEntityRemovalErrorTitle}
                    errorMessage={lastCartEntityRemovalErrorMessage}
                    backgroundColor="grey"
                />
            ) : null}
            <CashPull />
        </div>
    );
}
